import { useRef, useState } from 'react';
import { useProspectingAvailability } from './useProspectingAvailability';
import { Button, Checkbox, Dialog, InlineError, Input, Select } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useCopySets } from './hooks';
import { MessageSelectionDialog } from './MessageSelectionDialog';
import { copyMessageLabel, DRAFT_COPY_GROUP } from './copyMessages';
import type { CopySet } from './types';
import { messageCommand, messageSelectionError, useSaveMessageSelection } from './messageSelection';
import type { MessageCommand } from './messageSelection';
import {
  findLeadByEmail,
  prospectErrorMessage,
  prospectName,
  useBookCallForProspect,
  useDmCapStatus,
  useDmMessageCounts,
  useLogProspectDm,
  useRecordProspectMilestone,
} from './prospects';
import type { Prospect, ProspectMilestoneKind } from './prospects';

/** Kyu's 1-pager gate. Change this only when the overview is ready. */
export const OVERVIEW_READY = false;

export function DmDailyCap() {
  const cap = useDmCapStatus();
  if (cap.isError)
    return <InlineError>Today's DM count could not be loaded. Refresh to try again.</InlineError>;
  if (cap.data === undefined) return <p>Loading today's DM count...</p>;
  const { sent_today: sent, daily_cap: limit } = cap.data;
  const warn = sent >= Math.max(0, limit - 5);
  return (
    <div>
      <p>{`Sent today from Alex's account: ${String(sent)} / ${String(limit)}`}</p>
      {warn && (
        <p role="status" style={{ color: 'var(--state-warning)' }}>
          {sent >= limit
            ? 'Daily cap reached. Review the count before the next DM.'
            : 'Approaching the daily cap. Review the count before the next DM.'}
        </p>
      )}
    </div>
  );
}

/** The table mounts mutation/query observers only for the row being worked. */
export function ProspectRowDmActions({ prospect }: { prospect: Prospect }) {
  const [open, setOpen] = useState<'dm' | 'actions' | null>(null);
  return (
    <div>
      <Button size="sm" variant="secondary" onClick={() => setOpen('dm')}>
        Log DM
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setOpen(open === 'actions' ? null : 'actions')}
        aria-expanded={open === 'actions'}
      >
        Other actions
      </Button>
      {open === 'dm' && (
        <ProspectDmActions
          prospect={prospect}
          initialLogOpen
          logOnly
          onLogClose={() => setOpen(null)}
        />
      )}
      {open === 'actions' && <ProspectDmActions prospect={prospect} />}
    </div>
  );
}

export function ProspectDmActions({
  prospect,
  initialLogOpen = false,
  logOnly = false,
  onLogClose,
}: {
  prospect: Prospect;
  initialLogOpen?: boolean;
  logOnly?: boolean;
  onLogClose?: () => void;
}) {
  const { client } = useAuth();
  const log = useLogProspectDm();
  const milestone = useRecordProspectMilestone();
  const book = useBookCallForProspect();
  const [logOpen, setLogOpen] = useState(initialLogOpen);
  const [error, setError] = useState('');
  const [attach, setAttach] = useState<{ id: string; name: string } | null>(null);
  const [booked, setBooked] = useState(false);
  const pending = log.isPending || milestone.isPending || book.isPending;
  const promoted = prospect.promoted_at !== null;
  function closeLog() {
    setLogOpen(false);
    onLogClose?.();
  }
  function record(kind: ProspectMilestoneKind) {
    setError('');
    milestone.mutate(
      { prospectId: prospect.id, kind },
      { onError: (cause) => setError(prospectErrorMessage(cause.message)) },
    );
  }
  function bookCall(leadId?: string) {
    setError('');
    setAttach(null);
    book.mutate(
      { prospectId: prospect.id, ...(leadId === undefined ? {} : { leadId }) },
      {
        onSuccess: () => setBooked(true),
        onError: (cause) => {
          if (cause.message.includes('lead_email_exists')) {
            void findLeadByEmail(client, prospect.email)
              .then((lead) => {
                if (lead === null) setError(prospectErrorMessage(cause.message));
                else
                  setAttach({
                    id: lead.id,
                    name: `${lead.first_name} ${lead.last_name}`.trim() || prospect.email,
                  });
              })
              .catch((failure: unknown) =>
                setError(prospectErrorMessage(failure instanceof Error ? failure.message : '')),
              );
          } else setError(prospectErrorMessage(cause.message));
        },
      },
    );
  }
  return (
    <>
      {!logOnly && (
        <div className="prospect-drawer-actions">
          {!promoted && (
            <Button
              size="sm"
              variant="secondary"
              disabled={pending}
              onClick={() => {
                setError('');
                setLogOpen(true);
              }}
            >
              Log DM
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            disabled={pending}
            onClick={() => record('replied')}
          >
            Replied
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={pending}
            onClick={() => record('info_reply')}
          >
            Info reply
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!OVERVIEW_READY || pending}
            onClick={() => record('overview_sent')}
          >
            Overview sent
          </Button>
          {!OVERVIEW_READY && <span>Waits for the 1-pager</span>}
          <Button
            size="sm"
            variant="secondary"
            disabled={pending || booked}
            onClick={() => bookCall()}
          >
            Call booked
          </Button>
        </div>
      )}
      {error !== '' && <InlineError>{error}</InlineError>}
      {booked && <p role="status">Call booked. Work continues on the lead.</p>}
      {attach !== null && (
        <div className="prospect-drawer-actions">
          <span>
            A lead with this email already exists: <strong>{attach.name}</strong>.
          </span>
          <Button size="sm" disabled={pending} onClick={() => bookCall(attach.id)}>
            Attach to that lead and book call
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAttach(null)}>
            Cancel
          </Button>
        </div>
      )}
      {logOpen && <LogDmDialog prospect={prospect} log={log} onClose={closeLog} />}
    </>
  );
}

function LogDmDialog({
  prospect,
  log,
  onClose,
}: {
  prospect: Prospect;
  log: ReturnType<typeof useLogProspectDm>;
  onClose: () => void;
}) {
  const copies = useCopySets();
  const { message_enabled } = useProspectingAvailability();
  const versionedSession = useRef(message_enabled);
  if (message_enabled) versionedSession.current = true;
  const workflowPaused = versionedSession.current && !message_enabled;
  const selectMessage = useSaveMessageSelection();
  const counts = useDmMessageCounts();
  const messages = (copies.data ?? []).filter(
    (set) => set.channel === 'linkedin' && set.archived_at === null,
  );
  const [dmNumber, setDmNumber] = useState(1);
  const [copySetId, setCopySetId] = useState('');
  const [preview, setPreview] = useState<CopySet | null>(null);
  const [slot, setSlot] = useState(1);
  const [loggedId, setLoggedId] = useState<string | null>(null);
  const [retry, setRetry] = useState<MessageCommand | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [date, setDate] = useState('');
  const [sendAnyway, setSendAnyway] = useState(false);
  const [error, setError] = useState('');
  const chosen = message_enabled
    ? copySetId
    : copySetId === ''
      ? (messages[0]?.id ?? '')
      : copySetId;
  const pending = log.isPending || selectMessage.isPending;
  function saveSelection(command: MessageCommand) {
    setRetry(command);
    selectMessage.mutate(command, {
      onSuccess: onClose,
      onError: (cause) =>
        setError(
          `The DM was logged, but its message selection was not saved. ${messageSelectionError(cause.message)}`,
        ),
    });
  }
  function save() {
    // A versioned attempt owns its logged activity and receipt even if discovery
    // later fails. It can never become another legacy DM insertion.
    if (workflowPaused) return;
    if (!message_enabled) {
      if (prospect.tier === 'tier1' && !sendAnyway) return;
      if (chosen === '' || !messages.some((set) => set.id === chosen)) {
        setError('Choose an active LinkedIn message.');
        return;
      }
      setError('');
      log.mutate(
        {
          prospectId: prospect.id,
          dmNumber,
          copySetId: chosen,
          ...(date === '' ? {} : { occurredOn: date }),
        },
        {
          onSuccess: onClose,
          onError: (cause) => setError(prospectErrorMessage(cause.message)),
        },
      );
      return;
    }
    if (retry !== null) {
      saveSelection(retry);
      return;
    }
    if (loggedId !== null) return;
    if (prospect.tier === 'tier1' && !sendAnyway) return;
    if (
      chosen !== '' &&
      chosen !== '__paste__' &&
      (!preview?.message_revision ||
        !String(preview[`e${slot}_body` as keyof CopySet] ?? '').trim())
    ) {
      setError('Choose and preview an active LinkedIn message slot.');
      return;
    }
    setError('');
    log.mutate(
      {
        prospectId: prospect.id,
        dmNumber,
        // Logging and selecting are separate facts. Do not silently assign
        // today's wording before the revision-aware selection has succeeded.
        copySetId: null,
        ...(date === '' ? {} : { occurredOn: date }),
      },
      {
        onSuccess: (activityId) => {
          setLoggedId(activityId);
          if (chosen === '__paste__') {
            setPasteOpen(true);
            return;
          }
          if (preview === null) {
            onClose();
            return;
          }
          saveSelection(
            messageCommand(
              { kind: 'prospect', id: prospect.id },
              {
                id: activityId,
                copy_set_id: null,
                message_slot: null,
                message_selected_at: null,
                message_version: null,
              },
              { kind: 'library', copy: preview, slot },
              '',
              crypto.randomUUID(),
            ),
          );
        },
        onError: (cause) => setError(prospectErrorMessage(cause.message)),
      },
    );
  }
  if (message_enabled && pasteOpen && loggedId !== null)
    return (
      <MessageSelectionDialog
        subject={{ kind: 'prospect', id: prospect.id }}
        activity={{
          id: loggedId,
          copy_set_id: null,
          message_slot: null,
          message_selected_at: null,
          message_version: null,
        }}
        onClose={onClose}
      />
    );
  return (
    <Dialog
      open
      title="Log DM"
      onClose={() => {
        if (!pending) onClose();
      }}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            {loggedId === null ? 'Cancel' : 'Close'}
          </Button>
          <Button
            disabled={workflowPaused || pending || (loggedId !== null && retry === null)}
            onClick={save}
          >
            {retry !== null ? 'Retry message selection' : 'Save'}
          </Button>
        </>
      }
    >
      <p>{prospectName(prospect)} · Sent from Alex's account</p>
      {workflowPaused && (
        <p role="status">
          Message selection is paused because availability changed. Keep this dialog open to retry
          the same request when availability returns. No new DM will be logged.
        </p>
      )}
      {prospect.tier === 'tier1' && (
        <>
          <p>Tier 1 is on hold. Send anyway?</p>
          <Checkbox
            label="Send anyway"
            disabled={pending || loggedId !== null}
            checked={sendAnyway}
            onChange={(event) => setSendAnyway(event.target.checked)}
          />
        </>
      )}
      {error !== '' && <InlineError>{error}</InlineError>}
      {copies.isError && (
        <InlineError>Messages could not be loaded. Refresh to try again.</InlineError>
      )}
      {counts.isError && (
        <InlineError>Message counts could not be loaded. Refresh to try again.</InlineError>
      )}
      <Select
        label="DM number"
        disabled={pending || loggedId !== null}
        value={String(dmNumber)}
        options={[1, 2, 3].map((value) => ({ value: String(value), label: `DM ${String(value)}` }))}
        onChange={(event) => setDmNumber(Number(event.target.value))}
      />
      <Select
        label="Message used"
        disabled={pending || loggedId !== null}
        value={chosen}
        options={
          message_enabled
            ? [
                { value: '', label: 'Message not recorded' },
                { value: '__paste__', label: 'Paste a one-off (Draft)' },
                ...messages
                  .filter((set) => set.review_state !== 'draft')
                  .map((set) => ({
                    value: set.id,
                    label: `${copyMessageLabel(set)} (${counts.data === undefined ? '...' : String(counts.data.get(set.id) ?? 0)})`,
                  })),
              ]
            : messages.map((set) => ({
                value: set.id,
                label: `${set.name} (${counts.data === undefined ? '...' : String(counts.data.get(set.id) ?? 0)})`,
              }))
        }
        groups={
          message_enabled
            ? [
                {
                  label: DRAFT_COPY_GROUP,
                  options: messages
                    .filter((set) => set.review_state === 'draft')
                    .map((set) => ({ value: set.id, label: copyMessageLabel(set) })),
                },
              ]
            : []
        }
        onChange={(event) => {
          setCopySetId(event.target.value);
          const copy = messages.find((set) => set.id === event.target.value) ?? null;
          setPreview(copy);
          setSlot(
            copy === null
              ? 1
              : (Array.from({ length: 8 }, (_, i) => i + 1).find((n) =>
                  String(copy[`e${n}_body` as keyof CopySet] ?? '').trim(),
                ) ?? 1),
          );
        }}
      />
      {message_enabled && preview !== null && (
        <>
          <Select
            label="Message slot"
            value={String(slot)}
            disabled={pending || loggedId !== null}
            options={Array.from({ length: 8 }, (_, i) => i + 1)
              .filter((n) => String(preview[`e${n}_body` as keyof CopySet] ?? '').trim())
              .map((n) => ({ value: String(n), label: `Slot ${n}` }))}
            onChange={(e) => setSlot(Number(e.target.value))}
          />
          <p
            className="tracker-field-note"
            style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {String(preview[`e${slot}_body` as keyof CopySet] ?? '')}
          </p>
        </>
      )}
      {messages.length === 0 && !copies.isPending && (
        <p>Add an active LinkedIn message in the Copy Library first.</p>
      )}
      <Input
        type="date"
        label="Date (optional)"
        disabled={pending || loggedId !== null}
        value={date}
        onChange={(event) => setDate(event.target.value)}
      />
    </Dialog>
  );
}
